/**
 * Tree-person portrait helpers.
 *
 * The two family endpoints behind useFamilyTreeData describe the same tree person but name the
 * portrait fields differently (TOGV2-1765):
 *   - allaboutme `/getFamily`: `portraitThumbnailLink`, `portraitImageLink`
 *   - wicf `/family`: `imageLink` (the thumbnail href) plus a `links` object keyed
 *     `image-thumbnail` / `image` / `image-icon`
 * WICF also serializes `personId` as an object (`{ shortId, ... }`) where allaboutme sends a string.
 * Read persons through these helpers so a consumer never depends on which endpoint answered.
 */

export const getTreePersonId = (person) => {
  const personId = person?.personId
  if (!personId || typeof personId !== 'object') return personId ?? null
  return personId.shortId ?? personId.encodedIdValue ?? personId.fullyQualifiedId ?? null
}

export const getPortraitThumbnailUrl = (person) => {
  if (!person) return null
  return (
    person.portraitThumbnailLink ||
    person.imageLink ||
    person.links?.['image-thumbnail']?.href ||
    person.links?.['image-icon']?.href ||
    person.imageUrl ||
    null
  )
}

export const getPortraitImageUrl = (person) => {
  if (!person) return null
  return person.portraitImageLink || person.links?.image?.href || null
}

/**
 * Returns the person with `portraitThumbnailLink` / `portraitImageLink` filled in from whichever
 * fields the endpoint used. Returns the SAME object when nothing needs filling, so memoized
 * consumers keep their references.
 */
export const withPortraitLinks = (person) => {
  if (!person || typeof person !== 'object') return person
  const thumbnail = getPortraitThumbnailUrl(person)
  const image = getPortraitImageUrl(person)
  const needsThumbnail = Boolean(thumbnail) && person.portraitThumbnailLink !== thumbnail
  const needsImage = Boolean(image) && person.portraitImageLink !== image
  if (!needsThumbnail && !needsImage) return person
  return {
    ...person,
    ...(needsThumbnail ? { portraitThumbnailLink: thumbnail } : {}),
    ...(needsImage ? { portraitImageLink: image } : {}),
  }
}

/**
 * Maps a persons array through withPortraitLinks, returning the SAME array when no person changed.
 */
export const withPortraitLinksForAll = (persons) => {
  if (!Array.isArray(persons)) return persons
  let changed = false
  const mapped = persons.map((person) => {
    const next = withPortraitLinks(person)
    if (next !== person) changed = true
    return next
  })
  return changed ? mapped : persons
}
